import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LeadService } from '../services/lead.service';
import { SnackBarService } from '../services/snack-bar.service';

interface DebtInfo {
  bankName: string;
  balance: number;
}

interface MortgageInfo {
  type: string;
  value: number;
}

interface VehicleInfo {
  model: string;
  year: number;
  finance: number;
}

interface BusinessInfo {
  name: string;
  hst: string;
  articleOfIncorporation: string;
  debt: number;
}

interface FirstMeetingData {
  clientName: string;
  phoneNumber: string;
  emailId: string;
  debtInfo: DebtInfo[];
  assets: {
    firstMortgage: MortgageInfo[];
    secondMortgage: MortgageInfo[];
    propertyValue: number;
    lopNeeded: string;
  };
  vehicleInfo: VehicleInfo[];
  businessInfo: BusinessInfo;
  note: string;
}

@Component({
  selector: 'app-firstmeeting-create-popup',
  templateUrl: './firstmeeting-create-popup.component.html',
  styleUrls: ['./firstmeeting-create-popup.component.css']
})
export class FirstmeetingCreatePopupComponent implements OnInit {
  form: FormGroup;
  debtList: DebtInfo[] = [];
  firstMortgageList: MortgageInfo[] = [];
  secondMortgageList: MortgageInfo[] = [];
  vehicleList: VehicleInfo[] = [];
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<FirstmeetingCreatePopupComponent>,
    private router: Router,
    private leadService: LeadService,
    private snackBarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({
      clientName: ['', [Validators.required, Validators.minLength(2)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      emailId: ['', [Validators.required, Validators.email]],
      propertyValue: ['', [Validators.required, Validators.min(0)]],
      lopNeeded: ['no', Validators.required],
      businessName: [''],
      businessHst: [''],
      businessArticle: [''],
      businessDebt: ['', Validators.min(0)],
      note: ['']
    });
  }

  ngOnInit(): void {
    if (this.data) {
      this.form.patchValue(this.data);
    }
  }

  addDebt(): void {
    this.debtList.push({ bankName: '', balance: 0 });
  }

  removeDebt(index: number): void {
    this.debtList.splice(index, 1);
  }

  addFirstMortgage(): void {
    this.firstMortgageList.push({ type: 'first', value: 0 });
  }

  removeFirstMortgage(index: number): void {
    this.firstMortgageList.splice(index, 1);
  }

  addSecondMortgage(): void {
    this.secondMortgageList.push({ type: 'second', value: 0 });
  }

  removeSecondMortgage(index: number): void {
    this.secondMortgageList.splice(index, 1);
  }

  addVehicle(): void {
    this.vehicleList.push({ model: '', year: new Date().getFullYear(), finance: 0 });
  }

  removeVehicle(index: number): void {
    this.vehicleList.splice(index, 1);
  }

  onSubmit(): void {
    if (this.form.valid && this.validateAllSections()) {
      const formData: FirstMeetingData = {
        clientName: this.form.get('clientName')?.value,
        phoneNumber: this.form.get('phoneNumber')?.value,
        emailId: this.form.get('emailId')?.value,
        debtInfo: this.debtList,
        assets: {
          firstMortgage: this.firstMortgageList,
          secondMortgage: this.secondMortgageList,
          propertyValue: this.form.get('propertyValue')?.value,
          lopNeeded: this.form.get('lopNeeded')?.value
        },
        vehicleInfo: this.vehicleList,
        businessInfo: {
          name: this.form.get('businessName')?.value || '',
          hst: this.form.get('businessHst')?.value || '',
          articleOfIncorporation: this.form.get('businessArticle')?.value || '',
          debt: this.form.get('businessDebt')?.value || 0
        },
        note: this.form.get('note')?.value
      };

      this.isSubmitting = true;
      this.leadService.createFirstMeeting(formData).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          const meetingId = response.data?.id || response.id;
          this.snackBarService.openSnackBar('First meeting created successfully', 'success-class');
          this.dialogRef.close(formData);
          
          // Redirect to view page
          if (meetingId) {
            this.router.navigate(['/dashboard/firstmeeting/view', meetingId], {
              state: { data: formData }
            });
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error creating first meeting:', error);
          this.snackBarService.openSnackBar(
            error.error?.message || 'Error creating first meeting',
            'error-class'
          );
        }
      });
    }
  }

  validateAllSections(): boolean {
    return true;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getPhoneNumberError(): string {
    const control = this.form.get('phoneNumber');
    if (control?.hasError('required')) {
      return 'Phone number is required';
    }
    if (control?.hasError('pattern')) {
      return 'Phone number must be 10 digits';
    }
    return '';
  }

  getEmailError(): string {
    const control = this.form.get('emailId');
    if (control?.hasError('required')) {
      return 'Email is required';
    }
    if (control?.hasError('email')) {
      return 'Invalid email format';
    }
    return '';
  }
}
